import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, Stethoscope, Clock, CheckCircle2, AlertCircle, Download, ExternalLink } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import EngineerSidebar from '../Sidebar/EngineerSidebar';
import ChatBox from '../Chat/ChatBox';
import { consultingAPI } from '../../api';

const EngineerConsultingDetail = ({ user }) => {
  const { consultingId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [consulting, setConsulting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    loadConsulting();
  }, [consultingId]);

  const loadConsulting = async () => {
    try {
      const data = await consultingAPI.getConsulting(consultingId);
      setConsulting(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger le consulting",
        variant: "destructive"
      });
      navigate('/engineer/consultings');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await consultingAPI.updateConsultingStatus(consultingId, {
        status: newStatus,
        engineer_id: user.id
      });
      toast({
        title: "Succès",
        description: "Statut mis à jour"
      });
      loadConsulting();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le statut",
        variant: "destructive"
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!window.confirm("Confirmer que le paiement a bien été reçu sur Stripe ?")) return;
    setUpdatingStatus(true);
    try {
      await consultingAPI.updateConsultingStatus(consultingId, {
        payment_status: "paid",
        status: "in_progress"
      });
      toast({
        title: "Succès",
        description: "Paiement confirmé manuellement"
      });
      loadConsulting();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de confirmer le paiement",
        variant: "destructive"
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Terminé</Badge>;
      case 'in_progress':
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30"><Clock className="h-3 w-3 mr-1" />En cours</Badge>;
      case 'pending_payment':
        return <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30"><AlertCircle className="h-3 w-3 mr-1" />En attente paiement</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30"><AlertCircle className="h-3 w-3 mr-1" />En attente</Badge>;
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const blob = await consultingAPI.downloadFile(consultingId, fileId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le fichier",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111]">
        <EngineerSidebar />
        <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-[#A6A6A6]">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!consulting) return null;

  return (
    <div className="min-h-screen bg-[#111111]">
      <EngineerSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <Button
            variant="ghost"
            className="mb-6 text-[#A6A6A6] hover:text-white"
            onClick={() => navigate('/engineer/consultings')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux consultings
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Consulting Details */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-white text-2xl mb-2">
                        <Stethoscope className="inline h-6 w-6 mr-2 text-[#C00000]" />
                        {consulting.consulting_id}
                      </CardTitle>
                      <p className="text-[#A6A6A6]">
                        {consulting.vehicle_info.brand} {consulting.vehicle_info.model} {consulting.vehicle_info.engine}
                      </p>
                    </div>
                    {getStatusBadge(consulting.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Status Update */}
                    <div className="bg-[#111111] p-4 rounded-lg border border-[#A6A6A6]/20">
                      <h3 className="text-white font-semibold mb-3">Changer le statut</h3>
                      <Select 
                        value={consulting.status} 
                        onValueChange={handleStatusChange}
                        disabled={updatingStatus || consulting.payment_status !== 'paid'}
                      >
                        <SelectTrigger className="bg-[#1a1a1a] border-[#A6A6A6]/30 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a1a] border-[#A6A6A6]/30">
                          <SelectItem value="pending" className="text-white">En attente</SelectItem>
                          <SelectItem value="in_progress" className="text-white">En cours</SelectItem>
                          <SelectItem value="completed" className="text-white">Terminé</SelectItem>
                          <SelectItem value="cancelled" className="text-white">Annulé</SelectItem>
                        </SelectContent>
                      </Select>
                      {consulting.payment_status !== 'paid' && (
                        <div className="mt-2 space-y-2">
                          <p className="text-orange-500 text-sm">
                            ⚠️ En attente du paiement client
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-green-600 text-green-500 hover:bg-green-600/10"
                            onClick={handleMarkAsPaid}
                            disabled={updatingStatus}
                            data-testid="mark-as-paid-btn"
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Marquer comme payé (vérifier Stripe)
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Client Info */}
                    <div>
                      <h3 className="text-white font-semibold mb-2">Informations client</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-[#A6A6A6]">Nom:</span>
                          <p className="text-white">{consulting.client_name}</p>
                        </div>
                        <div>
                          <span className="text-[#A6A6A6]">Email:</span>
                          <p className="text-white">{consulting.client_email}</p>
                        </div>
                        {consulting.client_phone && (
                          <div>
                            <span className="text-[#A6A6A6]">Téléphone:</span>
                            <p className="text-white">{consulting.client_phone}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Vehicle Info */}
                    <div className="border-t border-[#A6A6A6]/20 pt-4">
                      <h3 className="text-white font-semibold mb-2">Informations du véhicule</h3>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        {consulting.vehicle_info.category && (
                          <div>
                            <span className="text-[#A6A6A6]">Catégorie :</span>
                            <p className="text-white">{consulting.vehicle_info.category}</p>
                          </div>
                        )}
                        <div>
                          <span className="text-[#A6A6A6]">Marque :</span>
                          <p className="text-white">{consulting.vehicle_info.brand}</p>
                        </div>
                        <div>
                          <span className="text-[#A6A6A6]">Modèle :</span>
                          <p className="text-white">{consulting.vehicle_info.model}</p>
                        </div>
                        {consulting.vehicle_info.year && (
                          <div>
                            <span className="text-[#A6A6A6]">Année :</span>
                            <p className="text-white">{consulting.vehicle_info.year}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.vin && (
                          <div>
                            <span className="text-[#A6A6A6]">N° de chassis :</span>
                            <p className="text-white font-mono">{consulting.vehicle_info.vin}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.license_plate && (
                          <div>
                            <span className="text-[#A6A6A6]">Immatriculation :</span>
                            <p className="text-white font-mono">{consulting.vehicle_info.license_plate}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.mileage && (
                          <div>
                            <span className="text-[#A6A6A6]">Kilométrage :</span>
                            <p className="text-white">{consulting.vehicle_info.mileage}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.engine && (
                          <div>
                            <span className="text-[#A6A6A6]">Motorisation :</span>
                            <p className="text-white">{consulting.vehicle_info.engine}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.engine_code && (
                          <div>
                            <span className="text-[#A6A6A6]">Code moteur :</span>
                            <p className="text-white">{consulting.vehicle_info.engine_code}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.power && (
                          <div>
                            <span className="text-[#A6A6A6]">Puissance :</span>
                            <p className="text-white">{consulting.vehicle_info.power} CV</p>
                          </div>
                        )}
                        {consulting.vehicle_info.fuel && (
                          <div>
                            <span className="text-[#A6A6A6]">Carburant :</span>
                            <p className="text-white">{consulting.vehicle_info.fuel}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.gearbox && (
                          <div>
                            <span className="text-[#A6A6A6]">Boîte :</span>
                            <p className="text-white">{consulting.vehicle_info.gearbox}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.transmission && (
                          <div>
                            <span className="text-[#A6A6A6]">Transmission :</span>
                            <p className="text-white">{consulting.vehicle_info.transmission}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {(consulting.vehicle_info.ecu_type || consulting.vehicle_info.ecu_tool ||
                      consulting.vehicle_info.file_type || consulting.vehicle_info.read_method) && (
                      <div className="border-t border-[#A6A6A6]/20 pt-4">
                        <h3 className="text-white font-semibold mb-2">Calculateur & Outil</h3>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          {consulting.vehicle_info.ecu_type && (
                            <div>
                              <span className="text-[#A6A6A6]">Type ECU :</span>
                              <p className="text-white">{consulting.vehicle_info.ecu_type}</p>
                            </div>
                          )}
                          {consulting.vehicle_info.ecu_tool && (
                            <div>
                              <span className="text-[#A6A6A6]">Outil utilisé :</span>
                              <p className="text-white">{consulting.vehicle_info.ecu_tool}</p>
                            </div>
                          )}
                          {consulting.vehicle_info.file_type && (
                            <div>
                              <span className="text-[#A6A6A6]">Type fichier :</span>
                              <p className="text-white">{consulting.vehicle_info.file_type}</p>
                            </div>
                          )}
                          {consulting.vehicle_info.read_method && (
                            <div>
                              <span className="text-[#A6A6A6]">Méthode lecture :</span>
                              <p className="text-white">{consulting.vehicle_info.read_method}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {consulting.vehicle_info.hardware_mods && consulting.vehicle_info.hardware_mods.length > 0 && (
                      <div className="border-t border-[#A6A6A6]/20 pt-4">
                        <h3 className="text-white font-semibold mb-2">Modifications hardware</h3>
                        <div className="flex flex-wrap gap-2">
                          {consulting.vehicle_info.hardware_mods.map((mod, i) => (
                            <Badge key={i} variant="outline" className="border-[#C00000]/50 text-white text-xs">
                              {mod}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Problem Description */}
                    <div className="border-t border-[#A6A6A6]/20 pt-4">
                      <h3 className="text-white font-semibold mb-2">Problème décrit</h3>
                      <div className="bg-[#111111] p-4 rounded-lg border border-[#A6A6A6]/20">
                        <p className="text-[#A6A6A6] text-sm whitespace-pre-wrap">{consulting.problem}</p>
                      </div>
                    </div>

                    {/* Files and Links */}
                    {(consulting.files?.length > 0 || consulting.links?.length > 0) && (
                      <div className="border-t border-[#A6A6A6]/20 pt-4">
                        <h3 className="text-white font-semibold mb-3">Fichiers diagnostics et liens</h3>
                        <div className="space-y-2">
                          {consulting.files?.map((file) => (
                            <div key={file.id} className="flex items-center justify-between bg-[#111111] p-3 rounded-lg">
                              <div>
                                <span className="text-white text-sm">{file.filename}</span>
                                <p className="text-[#A6A6A6] text-xs">Uploadé par {file.uploaded_by_name}</p>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-[#C00000] hover:text-[#C00000]/80"
                                onClick={() => handleDownload(file.id, file.filename)}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          {consulting.links?.map((link) => (
                            <div key={link.id} className="flex items-center justify-between bg-[#111111] p-3 rounded-lg">
                              <div className="flex-1 mr-4">
                                <span className="text-white text-sm truncate block">{link.url}</span>
                                <p className="text-[#A6A6A6] text-xs">Ajouté par {link.added_by_name}</p>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-[#C00000] hover:text-[#C00000]/80"
                                onClick={() => window.open(link.url, '_blank')}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Payment Info */}
                    <div className="border-t border-[#A6A6A6]/20 pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-[#A6A6A6]">Prix du consulting</span>
                        <span className="text-white font-bold text-xl">{consulting.price}€</span>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-[#A6A6A6]">Statut paiement</span>
                        <Badge className={consulting.payment_status === 'paid' ? 'bg-green-500/20 text-green-500' : 'bg-orange-500/20 text-orange-500'}>
                          {consulting.payment_status === 'paid' ? 'Payé' : 'En attente'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Chat */}
            <div className="lg:col-span-1">
              <ChatBox 
                orderId={consultingId}
                user={user}
                isConsulting={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngineerConsultingDetail;
